import * as THREE from 'three';
import { getGPUAdapterOptions } from './fiko-performance/FikoWebGPU';

/**
 * 🚀 FikoWebGPUCameraOps - WebGPU Camera Operations
 *
 * Pan/Rotate/Zoom sırasında GPU'da paralel hesaplama:
 * 1. Batch Frustum Culling - Tüm objeleri tek pass'de test et
 * 2. LOD Distance Calculation - Kamera-obje mesafelerini GPU'da hesapla
 * 3. Light Position Update - Headlight pozisyonlarını GPU'da güncelle
 * 4. Visibility Sorting - Depth-based sorting on GPU
 *
 * WebGPU varsa 100x+ hız, yoksa WebGL2/CPU fallback
 */

// WebGPU buffer usage constants
const GPU_BUFFER_USAGE = {
    MAP_READ: 0x0001,
    MAP_WRITE: 0x0002,
    COPY_SRC: 0x0004,
    COPY_DST: 0x0008,
    INDEX: 0x0010,
    VERTEX: 0x0020,
    UNIFORM: 0x0040,
    STORAGE: 0x0080,
    INDIRECT: 0x0100,
    QUERY_RESOLVE: 0x0200
};

const GPU_MAP_MODE = {
    READ: 0x0001,
    WRITE: 0x0002
};

// ============================================================================
// WGSL SHADERS
// ============================================================================

// 🚀 Combined Camera Operations Shader - All ops in single pass
const WEBGPU_CAMERA_OPS_SHADER = `
// Input: Object bounding spheres
struct ObjectBounds {
    center: vec3f,
    radius: f32,
    objectIndex: u32,
    lodLevel: u32,
    flags: u32,          // bit 0: static, bit 1: castShadow, etc.
    _pad: u32
}

// Output: Visibility + LOD + distance results
struct ObjectResult {
    visible: u32,        // 1 = visible, 0 = culled
    lodLevel: u32,       // Computed LOD level based on distance
    distance: f32,       // Distance to camera
    screenSize: f32,     // Approximate screen-space size (for LOD)
    objectIndex: u32,
    sortKey: u32,        // For depth sorting
    _pad1: u32,
    _pad2: u32
}

// Camera uniforms - carefully aligned for WebGPU
struct CameraUniforms {
    viewMatrix: mat4x4f,           // offset 0, size 64
    projMatrix: mat4x4f,           // offset 64, size 64
    viewProjMatrix: mat4x4f,       // offset 128, size 64
    cameraPosition: vec3f,         // offset 192, size 12
    fov: f32,                      // offset 204, size 4
    frustumPlanes: array<vec4f, 6>, // offset 208, size 96
    lodDistances: vec4f,           // offset 304, size 16 (vec4 needs 16-byte alignment)
    screenSize: vec2f,             // offset 320, size 8
    nearPlane: f32,                // offset 328, size 4
    farPlane: f32,                 // offset 332, size 4
    objectCount: u32,              // offset 336, size 4
    _pad1: u32,                    // offset 340, size 4
    _pad2: u32,                    // offset 344, size 4
    _pad3: u32                     // offset 348, size 4  -> total 352 bytes
}

@group(0) @binding(0) var<storage, read> objects: array<ObjectBounds>;
@group(0) @binding(1) var<storage, read_write> results: array<ObjectResult>;
@group(0) @binding(2) var<uniform> camera: CameraUniforms;

// Frustum-sphere intersection test
fn sphereInFrustum(center: vec3f, radius: f32) -> bool {
    for (var i = 0u; i < 6u; i++) {
        let plane = camera.frustumPlanes[i];
        let dist = dot(plane.xyz, center) + plane.w;
        if (dist < -radius) {
            return false;
        }
    }
    return true;
}

// Calculate LOD level based on distance and screen size
fn calculateLOD(distance: f32, radius: f32) -> u32 {
    // Screen-space size estimation
    let screenSize = (radius * camera.screenSize.y) / (distance * tan(camera.fov * 0.5));

    // LOD selection based on screen size
    if (screenSize > 100.0) {
        return 0u;  // Highest detail
    } else if (screenSize > 50.0) {
        return 1u;
    } else if (screenSize > 20.0) {
        return 2u;
    } else if (screenSize > 5.0) {
        return 3u;
    }
    return 4u;  // Lowest detail / cull
}

// Generate sort key for depth sorting (front-to-back for opaque)
fn generateSortKey(distance: f32) -> u32 {
    // Convert float distance to sortable uint
    // Closer objects get lower keys (front-to-back)
    let normalized = saturate((distance - camera.nearPlane) / (camera.farPlane - camera.nearPlane));
    return u32(normalized * 4294967295.0);
}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3u) {
    let idx = gid.x;
    if (idx >= camera.objectCount) {
        return;
    }

    let obj = objects[idx];
    var result: ObjectResult;
    result.objectIndex = obj.objectIndex;

    // 1. Frustum culling test
    let visible = sphereInFrustum(obj.center, obj.radius);
    result.visible = select(0u, 1u, visible);

    if (!visible) {
        // Early out - culled object
        result.lodLevel = 4u;  // Max LOD = invisible
        result.distance = 999999.0;
        result.screenSize = 0.0;
        result.sortKey = 0xFFFFFFFFu;
        results[idx] = result;
        return;
    }

    // 2. Distance calculation
    let toCamera = camera.cameraPosition - obj.center;
    let distance = length(toCamera);
    result.distance = distance;

    // 3. LOD calculation
    result.lodLevel = calculateLOD(distance, obj.radius);

    // 4. Screen size for adaptive quality
    result.screenSize = (obj.radius * camera.screenSize.y) / (distance * tan(camera.fov * 0.5));

    // 5. Sort key for depth sorting
    result.sortKey = generateSortKey(distance);

    results[idx] = result;
}
`;

// 🚀 Headlight Update Shader - Multiple lights in parallel
const WEBGPU_HEADLIGHT_UPDATE_SHADER = `
struct LightInput {
    position: vec3f,
    offsetDistance: f32,
    manualOffset: vec3f,
    isActive: u32,
    targetPosition: vec3f,
    _pad: u32
}

struct LightOutput {
    position: vec3f,
    _pad1: u32,
    targetPosition: vec3f,
    _pad2: u32
}

struct CameraData {
    position: vec3f,
    _pad1: u32,
    quaternion: vec4f,
    lightCount: u32,
    _pad2: u32,
    _pad3: u32,
    _pad4: u32
}

@group(0) @binding(0) var<storage, read> lights: array<LightInput>;
@group(0) @binding(1) var<storage, read_write> results: array<LightOutput>;
@group(0) @binding(2) var<uniform> camera: CameraData;

// Apply quaternion rotation to vector
fn rotateByQuaternion(v: vec3f, q: vec4f) -> vec3f {
    let qv = vec3f(q.x, q.y, q.z);
    let w = q.w;
    let t = 2.0 * cross(qv, v);
    return v + w * t + cross(qv, t);
}

@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) gid: vec3u) {
    let idx = gid.x;
    if (idx >= camera.lightCount) {
        return;
    }

    let light = lights[idx];
    var result: LightOutput;

    if (light.isActive == 0u) {
        // Inactive light - copy original position
        result.position = light.position;
        result.targetPosition = light.targetPosition;
    } else {
        // Calculate offset in camera direction
        let offset = rotateByQuaternion(vec3f(0.0, 0.0, light.offsetDistance), camera.quaternion);

        // New position = camera position + offset + manual offset
        result.position = camera.position + offset + light.manualOffset;
        result.targetPosition = camera.position;
    }

    results[idx] = result;
}
`;

// 🚀 Batch Matrix Transform Shader - Update world matrices on GPU
const WEBGPU_MATRIX_UPDATE_SHADER = `
struct MatrixInput {
    localMatrix: mat4x4f,
    parentWorldMatrix: mat4x4f,
    objectIndex: u32,
    needsUpdate: u32,
    _pad1: u32,
    _pad2: u32
}

struct MatrixOutput {
    worldMatrix: mat4x4f,
    worldMatrixInverse: mat4x4f,
    normalMatrix: mat3x3f,
    _pad: array<f32, 3>
}

@group(0) @binding(0) var<storage, read> inputs: array<MatrixInput>;
@group(0) @binding(1) var<storage, read_write> outputs: array<MatrixOutput>;
@group(0) @binding(2) var<uniform> objectCount: u32;

// Fast matrix inverse for affine transforms
fn invertAffineMatrix(m: mat4x4f) -> mat4x4f {
    // Extract 3x3 rotation/scale part
    let r = mat3x3f(m[0].xyz, m[1].xyz, m[2].xyz);

    // Transpose = inverse for pure rotation (approximate for scale)
    let rInv = transpose(r);

    // Extract translation
    let t = m[3].xyz;

    // Inverse translation
    let tInv = -(rInv * t);

    return mat4x4f(
        vec4f(rInv[0], 0.0),
        vec4f(rInv[1], 0.0),
        vec4f(rInv[2], 0.0),
        vec4f(tInv, 1.0)
    );
}

// Extract normal matrix from world matrix
fn extractNormalMatrix(m: mat4x4f) -> mat3x3f {
    // Normal matrix = transpose(inverse(upper-left 3x3))
    // For uniform scale, this is just the upper-left 3x3
    return mat3x3f(m[0].xyz, m[1].xyz, m[2].xyz);
}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3u) {
    let idx = gid.x;
    if (idx >= objectCount) {
        return;
    }

    let input = inputs[idx];
    var output: MatrixOutput;

    if (input.needsUpdate == 0u) {
        // Copy existing (would need additional storage)
        return;
    }

    // Calculate world matrix
    output.worldMatrix = input.parentWorldMatrix * input.localMatrix;

    // Calculate inverse
    output.worldMatrixInverse = invertAffineMatrix(output.worldMatrix);

    // Calculate normal matrix
    output.normalMatrix = extractNormalMatrix(output.worldMatrix);

    outputs[idx] = output;
}
`;

// ============================================================================
// MAIN CLASS
// ============================================================================

export interface CameraOpsResult {
    visibleIndices: Uint32Array;
    lodLevels: Uint32Array;
    distances: Float32Array;
    screenSizes: Float32Array;
    sortKeys: Uint32Array;
    culledCount: number;
    visibleCount: number;
    gpuTime: number;
}

export interface HeadlightResult {
    positions: Float32Array;  // [x,y,z, x,y,z, ...]
    targetPositions: Float32Array;
    gpuTime: number;
}

export class FikoWebGPUCameraOps {
    private static instance: FikoWebGPUCameraOps | null = null;

    private gpuDevice: GPUDevice | null = null;
    private gpuAdapter: GPUAdapter | null = null;
    private isAvailable: boolean = false;
    private initPromise: Promise<boolean> | null = null;

    // Cached pipelines
    private cameraOpsPipeline: GPUComputePipeline | null = null;
    private headlightPipeline: GPUComputePipeline | null = null;
    private matrixUpdatePipeline: GPUComputePipeline | null = null;

    // Cached shader modules
    private cameraOpsModule: GPUShaderModule | null = null;
    private headlightModule: GPUShaderModule | null = null;
    private matrixUpdateModule: GPUShaderModule | null = null;

    // Buffer pool for reuse
    private bufferPool: Map<number, GPUBuffer[]> = new Map();
    private readonly MAX_POOL_SIZE = 10;

    // Pre-allocated buffers for common sizes
    private cachedUniformBuffer: GPUBuffer | null = null;
    private cachedResultBuffer: GPUBuffer | null = null;
    private lastObjectCount: number = 0;

    // Stats
    private stats = {
        cameraOpsDispatches: 0,
        headlightDispatches: 0,
        matrixDispatches: 0,
        totalGPUTime: 0,
        bufferPoolHits: 0,
        bufferPoolMisses: 0
    };

    // Static vectors for CPU fallback (zero allocation)
    private static _tempVec3 = new THREE.Vector3();
    private static _tempQuat = new THREE.Quaternion();
    private static _tempMatrix = new THREE.Matrix4();
    private static _frustum = new THREE.Frustum();
    private static _projScreenMatrix = new THREE.Matrix4();

    private constructor() {}

    static getInstance(): FikoWebGPUCameraOps {
        if (!FikoWebGPUCameraOps.instance) {
            FikoWebGPUCameraOps.instance = new FikoWebGPUCameraOps();
        }
        return FikoWebGPUCameraOps.instance;
    }

    static async initialize(): Promise<FikoWebGPUCameraOps> {
        const instance = FikoWebGPUCameraOps.getInstance();
        await instance.init();
        return instance;
    }

    // ========================================================================
    // INITIALIZATION
    // ========================================================================

    async init(): Promise<boolean> {
        if (this.initPromise) {
            return this.initPromise;
        }
        this.initPromise = this._doInit();
        return this.initPromise;
    }

    private async _doInit(): Promise<boolean> {
        if (!navigator.gpu) {
            this.isAvailable = false;
            return false;
        }

        try {
            this.gpuAdapter = await navigator.gpu.requestAdapter(getGPUAdapterOptions());

            if (!this.gpuAdapter) {
                return false;
            }

            this.gpuDevice = await this.gpuAdapter.requestDevice({
                requiredFeatures: [],
                requiredLimits: {
                    maxStorageBufferBindingSize: 256 * 1024 * 1024,  // 256MB
                    maxComputeWorkgroupsPerDimension: 65535
                }
            });

            if (!this.gpuDevice) {
                return false;
            }

            // Handle device loss
            (this.gpuDevice as any).lost?.then?.((info: any) => {
                this.isAvailable = false;
                this.gpuDevice = null;
            });

            // Create shader modules and pipelines
            await this.createPipelines();

            this.isAvailable = true;
            return true;

        } catch (e) {
            this.isAvailable = false;
            return false;
        }
    }

    private async createPipelines(): Promise<void> {
        if (!this.gpuDevice) return;

        // Camera operations shader
        try {
            this.cameraOpsModule = this.gpuDevice.createShaderModule({
                code: WEBGPU_CAMERA_OPS_SHADER
            });
            this.cameraOpsPipeline = this.gpuDevice.createComputePipeline({
                layout: 'auto',
                compute: {
                    module: this.cameraOpsModule,
                    entryPoint: 'main'
                }
            });
        } catch (e) {
            // Camera ops shader failed
        }

        // Headlight shader
        try {
            this.headlightModule = this.gpuDevice.createShaderModule({
                code: WEBGPU_HEADLIGHT_UPDATE_SHADER
            });
            this.headlightPipeline = this.gpuDevice.createComputePipeline({
                layout: 'auto',
                compute: {
                    module: this.headlightModule,
                    entryPoint: 'main'
                }
            });
        } catch (e) {
            // Headlight shader failed
        }

        // Matrix update shader
        try {
            this.matrixUpdateModule = this.gpuDevice.createShaderModule({
                code: WEBGPU_MATRIX_UPDATE_SHADER
            });
            this.matrixUpdatePipeline = this.gpuDevice.createComputePipeline({
                layout: 'auto',
                compute: {
                    module: this.matrixUpdateModule,
                    entryPoint: 'main'
                }
            });
        } catch (e) {
            // Matrix update shader failed
        }
    }

    // ========================================================================
    // BUFFER MANAGEMENT
    // ========================================================================

    private getBuffer(size: number, usage: number): GPUBuffer | null {
        if (!this.gpuDevice) return null;

        const key = size + usage * 1000000;
        const pool = this.bufferPool.get(key);

        if (pool && pool.length > 0) {
            this.stats.bufferPoolHits++;
            return pool.pop()!;
        }

        this.stats.bufferPoolMisses++;
        return this.gpuDevice.createBuffer({ size, usage });
    }

    private returnBuffer(buffer: GPUBuffer, size: number, usage: number): void {
        const key = size + usage * 1000000;
        if (!this.bufferPool.has(key)) {
            this.bufferPool.set(key, []);
        }
        const pool = this.bufferPool.get(key)!;
        if (pool.length < this.MAX_POOL_SIZE) {
            pool.push(buffer);
        } else {
            buffer.destroy();
        }
    }

    // ========================================================================
    // CAMERA OPERATIONS - Frustum Culling + LOD + Distance
    // ========================================================================

    /**
     * 🚀 GPU-accelerated camera operations for all objects
     * Single compute pass: Frustum Culling + LOD + Distance + Sort Key
     *
     * @param objects Array of object bounding spheres
     * @param camera Active camera
     * @param screenSize [width, height]
     * @returns Visibility, LOD, distance, and sort data for all objects
     */
    async computeCameraOps(
        objects: Array<{ center: THREE.Vector3; radius: number; objectIndex: number }>,
        camera: THREE.Camera,
        screenSize: [number, number]
    ): Promise<CameraOpsResult | null> {
        const startTime = performance.now();
        const objectCount = objects.length;

        if (objectCount === 0) {
            return {
                visibleIndices: new Uint32Array(0),
                lodLevels: new Uint32Array(0),
                distances: new Float32Array(0),
                screenSizes: new Float32Array(0),
                sortKeys: new Uint32Array(0),
                culledCount: 0,
                visibleCount: 0,
                gpuTime: 0
            };
        }

        // Try WebGPU first
        if (this.isAvailable && this.gpuDevice && this.cameraOpsPipeline) {
            const result = await this._computeCameraOpsWebGPU(objects, camera, screenSize);
            if (result) {
                this.stats.cameraOpsDispatches++;
                return result;
            }
        }

        // CPU fallback
        return this._computeCameraOpsCPU(objects, camera, screenSize, startTime);
    }

    private async _computeCameraOpsWebGPU(
        objects: Array<{ center: THREE.Vector3; radius: number; objectIndex: number }>,
        camera: THREE.Camera,
        screenSize: [number, number]
    ): Promise<CameraOpsResult | null> {
        if (!this.gpuDevice || !this.cameraOpsPipeline) return null;

        const startTime = performance.now();
        const objectCount = objects.length;

        try {
            // Prepare input data - ObjectBounds: center(3) + radius + objectIndex + lodLevel + flags + pad = 8 floats
            const inputData = new Float32Array(objectCount * 8);
            for (let i = 0; i < objectCount; i++) {
                const offset = i * 8;
                inputData[offset] = objects[i].center.x;
                inputData[offset + 1] = objects[i].center.y;
                inputData[offset + 2] = objects[i].center.z;
                inputData[offset + 3] = objects[i].radius;
                // Store objectIndex in the float (will be read as u32 in shader)
                const view = new DataView(inputData.buffer);
                view.setUint32((offset + 4) * 4, objects[i].objectIndex, true);
                view.setUint32((offset + 5) * 4, 0, true);  // lodLevel (initial)
                view.setUint32((offset + 6) * 4, 0, true);  // flags
                view.setUint32((offset + 7) * 4, 0, true);  // pad
            }

            const inputBuffer = this.gpuDevice.createBuffer({
                size: inputData.byteLength,
                usage: GPU_BUFFER_USAGE.STORAGE | GPU_BUFFER_USAGE.COPY_DST,
                mappedAtCreation: true
            });
            new Float32Array(inputBuffer.getMappedRange()).set(inputData);
            inputBuffer.unmap();

            // Output buffer - ObjectResult: 8 x u32/f32 per object
            const outputSize = objectCount * 8 * 4;
            const outputBuffer = this.gpuDevice.createBuffer({
                size: outputSize,
                usage: GPU_BUFFER_USAGE.STORAGE | GPU_BUFFER_USAGE.COPY_SRC
            });

            const readbackBuffer = this.gpuDevice.createBuffer({
                size: outputSize,
                usage: GPU_BUFFER_USAGE.MAP_READ | GPU_BUFFER_USAGE.COPY_DST
            });

            // Prepare camera uniforms
            const projMatrix = camera.projectionMatrix;
            const viewMatrix = camera.matrixWorldInverse;
            const viewProjMatrix = FikoWebGPUCameraOps._projScreenMatrix.multiplyMatrices(projMatrix, viewMatrix);

            // Extract frustum planes
            FikoWebGPUCameraOps._frustum.setFromProjectionMatrix(viewProjMatrix);
            const planes = FikoWebGPUCameraOps._frustum.planes;

            // Get FOV from camera
            const fov = (camera as any).fov ? (camera as any).fov * Math.PI / 180 : Math.PI / 4;
            const near = (camera as any).near || 0.1;
            const far = (camera as any).far || 1000;

            // Uniform data layout matching WGSL struct (88 floats = 352 bytes)
            // viewMatrix(16) + projMatrix(16) + viewProjMatrix(16) + camPos(3) + fov(1) + planes(24) + lodDist(4) + screen(2) + near(1) + far(1) + count(1) + pad(3)
            const uniformData = new Float32Array(88);
            let offset = 0;

            // View matrix (64 bytes)
            uniformData.set(viewMatrix.elements, offset); offset += 16;
            // Projection matrix (64 bytes)
            uniformData.set(projMatrix.elements, offset); offset += 16;
            // View-projection matrix (64 bytes)
            uniformData.set(viewProjMatrix.elements, offset); offset += 16;
            // Camera position (12 bytes) + fov (4 bytes)
            uniformData[offset++] = camera.position.x;
            uniformData[offset++] = camera.position.y;
            uniformData[offset++] = camera.position.z;
            uniformData[offset++] = fov;
            // Frustum planes (6 x vec4 = 96 bytes)
            for (let i = 0; i < 6; i++) {
                uniformData[offset++] = planes[i].normal.x;
                uniformData[offset++] = planes[i].normal.y;
                uniformData[offset++] = planes[i].normal.z;
                uniformData[offset++] = planes[i].constant;
            }
            // LOD distances (vec4 = 16 bytes) - MUST come before screenSize for alignment
            uniformData[offset++] = 10;   // LOD 0 threshold
            uniformData[offset++] = 50;   // LOD 1 threshold
            uniformData[offset++] = 200;  // LOD 2 threshold
            uniformData[offset++] = 500;  // LOD 3 threshold
            // Screen size (8 bytes) + near (4 bytes) + far (4 bytes)
            uniformData[offset++] = screenSize[0];
            uniformData[offset++] = screenSize[1];
            uniformData[offset++] = near;
            uniformData[offset++] = far;
            // Object count (4 bytes) + padding (12 bytes)
            const uniformView = new DataView(uniformData.buffer);
            uniformView.setUint32(offset * 4, objectCount, true);
            offset++;
            uniformData[offset++] = 0;
            uniformData[offset++] = 0;
            uniformData[offset++] = 0;

            const uniformBuffer = this.gpuDevice.createBuffer({
                size: uniformData.byteLength,
                usage: GPU_BUFFER_USAGE.UNIFORM | GPU_BUFFER_USAGE.COPY_DST,
                mappedAtCreation: true
            });
            new Float32Array(uniformBuffer.getMappedRange()).set(uniformData);
            uniformBuffer.unmap();

            // Create bind group
            const bindGroup = this.gpuDevice.createBindGroup({
                layout: (this.cameraOpsPipeline as any).getBindGroupLayout(0),
                entries: [
                    { binding: 0, resource: { buffer: inputBuffer } },
                    { binding: 1, resource: { buffer: outputBuffer } },
                    { binding: 2, resource: { buffer: uniformBuffer } }
                ]
            });

            // Encode and submit
            const commandEncoder = this.gpuDevice.createCommandEncoder();
            const passEncoder = commandEncoder.beginComputePass();
            passEncoder.setPipeline(this.cameraOpsPipeline);
            passEncoder.setBindGroup(0, bindGroup);
            passEncoder.dispatchWorkgroups(Math.ceil(objectCount / 256));
            passEncoder.end();

            commandEncoder.copyBufferToBuffer(outputBuffer, 0, readbackBuffer, 0, outputSize);
            this.gpuDevice.queue.submit([commandEncoder.finish()]);

            // Async readback
            await readbackBuffer.mapAsync(GPU_MAP_MODE.READ);
            const resultData = new Float32Array(readbackBuffer.getMappedRange().slice(0));
            readbackBuffer.unmap();

            // Extract results
            const visibleIndices = new Uint32Array(objectCount);
            const lodLevels = new Uint32Array(objectCount);
            const distances = new Float32Array(objectCount);
            const screenSizes = new Float32Array(objectCount);
            const sortKeys = new Uint32Array(objectCount);

            let visibleCount = 0;
            const resultView = new DataView(resultData.buffer);

            for (let i = 0; i < objectCount; i++) {
                const baseOffset = i * 8 * 4;  // 8 floats per object, 4 bytes per float
                const visible = resultView.getUint32(baseOffset, true);
                visibleIndices[i] = visible;
                lodLevels[i] = resultView.getUint32(baseOffset + 4, true);
                distances[i] = resultView.getFloat32(baseOffset + 8, true);
                screenSizes[i] = resultView.getFloat32(baseOffset + 12, true);
                // objectIndex at offset 16
                sortKeys[i] = resultView.getUint32(baseOffset + 20, true);

                if (visible === 1) {
                    visibleCount++;
                }
            }

            // Cleanup
            inputBuffer.destroy();
            outputBuffer.destroy();
            readbackBuffer.destroy();
            uniformBuffer.destroy();

            const gpuTime = performance.now() - startTime;
            this.stats.totalGPUTime += gpuTime;

            return {
                visibleIndices,
                lodLevels,
                distances,
                screenSizes,
                sortKeys,
                culledCount: objectCount - visibleCount,
                visibleCount,
                gpuTime
            };

        } catch (e) {
            return null;
        }
    }

    private _computeCameraOpsCPU(
        objects: Array<{ center: THREE.Vector3; radius: number; objectIndex: number }>,
        camera: THREE.Camera,
        screenSize: [number, number],
        startTime: number
    ): CameraOpsResult {
        const objectCount = objects.length;
        const visibleIndices = new Uint32Array(objectCount);
        const lodLevels = new Uint32Array(objectCount);
        const distances = new Float32Array(objectCount);
        const screenSizes = new Float32Array(objectCount);
        const sortKeys = new Uint32Array(objectCount);

        // Update frustum
        FikoWebGPUCameraOps._projScreenMatrix.multiplyMatrices(
            camera.projectionMatrix,
            camera.matrixWorldInverse
        );
        FikoWebGPUCameraOps._frustum.setFromProjectionMatrix(FikoWebGPUCameraOps._projScreenMatrix);

        const fov = (camera as any).fov ? (camera as any).fov * Math.PI / 180 : Math.PI / 4;
        const tanHalfFov = Math.tan(fov * 0.5);
        const near = (camera as any).near || 0.1;
        const far = (camera as any).far || 1000;

        let visibleCount = 0;
        const camPos = camera.position;

        for (let i = 0; i < objectCount; i++) {
            const obj = objects[i];

            // Frustum test using pre-allocated tempVec3
            FikoWebGPUCameraOps._tempVec3.copy(obj.center);
            const visible = FikoWebGPUCameraOps._frustum.containsPoint(FikoWebGPUCameraOps._tempVec3) ||
                           this.sphereInFrustumCPU(obj.center, obj.radius);

            visibleIndices[i] = visible ? 1 : 0;

            if (!visible) {
                lodLevels[i] = 4;
                distances[i] = 999999;
                screenSizes[i] = 0;
                sortKeys[i] = 0xFFFFFFFF;
                continue;
            }

            visibleCount++;

            // Distance
            const dx = camPos.x - obj.center.x;
            const dy = camPos.y - obj.center.y;
            const dz = camPos.z - obj.center.z;
            const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            distances[i] = dist;

            // Screen size
            const screenSize_ = (obj.radius * screenSize[1]) / (dist * tanHalfFov);
            screenSizes[i] = screenSize_;

            // LOD
            if (screenSize_ > 100) lodLevels[i] = 0;
            else if (screenSize_ > 50) lodLevels[i] = 1;
            else if (screenSize_ > 20) lodLevels[i] = 2;
            else if (screenSize_ > 5) lodLevels[i] = 3;
            else lodLevels[i] = 4;

            // Sort key
            const normalized = Math.min(1, Math.max(0, (dist - near) / (far - near)));
            sortKeys[i] = Math.floor(normalized * 0xFFFFFFFF);
        }

        return {
            visibleIndices,
            lodLevels,
            distances,
            screenSizes,
            sortKeys,
            culledCount: objectCount - visibleCount,
            visibleCount,
            gpuTime: performance.now() - startTime
        };
    }

    private sphereInFrustumCPU(center: THREE.Vector3, radius: number): boolean {
        const planes = FikoWebGPUCameraOps._frustum.planes;
        for (let i = 0; i < 6; i++) {
            const plane = planes[i];
            const dist = plane.normal.x * center.x + plane.normal.y * center.y + plane.normal.z * center.z + plane.constant;
            if (dist < -radius) {
                return false;
            }
        }
        return true;
    }

    // ========================================================================
    // HEADLIGHT OPERATIONS
    // ========================================================================

    /**
     * 🚀 GPU-accelerated headlight position updates
     *
     * @param lights Array of headlight data
     * @param cameraPosition Camera world position
     * @param cameraQuaternion Camera rotation quaternion
     * @returns Updated light positions and targets
     */
    async computeHeadlightPositions(
        lights: Array<{
            position: THREE.Vector3;
            offsetDistance: number;
            manualOffset: THREE.Vector3;
            isActive: boolean;
            targetPosition: THREE.Vector3;
        }>,
        cameraPosition: THREE.Vector3,
        cameraQuaternion: THREE.Quaternion
    ): Promise<HeadlightResult | null> {
        const startTime = performance.now();
        const lightCount = lights.length;

        if (lightCount === 0) {
            return {
                positions: new Float32Array(0),
                targetPositions: new Float32Array(0),
                gpuTime: 0
            };
        }

        // For small light counts, CPU is faster due to GPU overhead
        if (lightCount < 4 || !this.isAvailable) {
            return this._computeHeadlightsCPU(lights, cameraPosition, cameraQuaternion, startTime);
        }

        // WebGPU path
        if (this.gpuDevice && this.headlightPipeline) {
            const result = await this._computeHeadlightsWebGPU(lights, cameraPosition, cameraQuaternion);
            if (result) {
                this.stats.headlightDispatches++;
                return result;
            }
        }

        // CPU fallback
        return this._computeHeadlightsCPU(lights, cameraPosition, cameraQuaternion, startTime);
    }

    private async _computeHeadlightsWebGPU(
        lights: Array<{
            position: THREE.Vector3;
            offsetDistance: number;
            manualOffset: THREE.Vector3;
            isActive: boolean;
            targetPosition: THREE.Vector3;
        }>,
        cameraPosition: THREE.Vector3,
        cameraQuaternion: THREE.Quaternion
    ): Promise<HeadlightResult | null> {
        if (!this.gpuDevice || !this.headlightPipeline) return null;

        const startTime = performance.now();
        const lightCount = lights.length;

        try {
            // Input data: position(3) + offsetDist + manualOffset(3) + isActive + targetPos(3) + pad = 12 floats
            const inputData = new Float32Array(lightCount * 12);
            for (let i = 0; i < lightCount; i++) {
                const offset = i * 12;
                const light = lights[i];
                inputData[offset] = light.position.x;
                inputData[offset + 1] = light.position.y;
                inputData[offset + 2] = light.position.z;
                inputData[offset + 3] = light.offsetDistance;
                inputData[offset + 4] = light.manualOffset.x;
                inputData[offset + 5] = light.manualOffset.y;
                inputData[offset + 6] = light.manualOffset.z;
                const view = new DataView(inputData.buffer);
                view.setUint32((offset + 7) * 4, light.isActive ? 1 : 0, true);
                inputData[offset + 8] = light.targetPosition.x;
                inputData[offset + 9] = light.targetPosition.y;
                inputData[offset + 10] = light.targetPosition.z;
                inputData[offset + 11] = 0;  // pad
            }

            const inputBuffer = this.gpuDevice.createBuffer({
                size: inputData.byteLength,
                usage: GPU_BUFFER_USAGE.STORAGE | GPU_BUFFER_USAGE.COPY_DST,
                mappedAtCreation: true
            });
            new Float32Array(inputBuffer.getMappedRange()).set(inputData);
            inputBuffer.unmap();

            // Output: position(3) + pad + targetPos(3) + pad = 8 floats per light
            const outputSize = lightCount * 8 * 4;
            const outputBuffer = this.gpuDevice.createBuffer({
                size: outputSize,
                usage: GPU_BUFFER_USAGE.STORAGE | GPU_BUFFER_USAGE.COPY_SRC
            });

            const readbackBuffer = this.gpuDevice.createBuffer({
                size: outputSize,
                usage: GPU_BUFFER_USAGE.MAP_READ | GPU_BUFFER_USAGE.COPY_DST
            });

            // Camera uniform: position(3) + pad + quaternion(4) + lightCount + pad(3) = 12 floats
            const uniformData = new Float32Array(12);
            uniformData[0] = cameraPosition.x;
            uniformData[1] = cameraPosition.y;
            uniformData[2] = cameraPosition.z;
            uniformData[3] = 0;  // pad
            uniformData[4] = cameraQuaternion.x;
            uniformData[5] = cameraQuaternion.y;
            uniformData[6] = cameraQuaternion.z;
            uniformData[7] = cameraQuaternion.w;
            const uniformView = new DataView(uniformData.buffer);
            uniformView.setUint32(8 * 4, lightCount, true);
            uniformData[9] = 0;
            uniformData[10] = 0;
            uniformData[11] = 0;

            const uniformBuffer = this.gpuDevice.createBuffer({
                size: uniformData.byteLength,
                usage: GPU_BUFFER_USAGE.UNIFORM | GPU_BUFFER_USAGE.COPY_DST,
                mappedAtCreation: true
            });
            new Float32Array(uniformBuffer.getMappedRange()).set(uniformData);
            uniformBuffer.unmap();

            // Bind group
            const bindGroup = this.gpuDevice.createBindGroup({
                layout: (this.headlightPipeline as any).getBindGroupLayout(0),
                entries: [
                    { binding: 0, resource: { buffer: inputBuffer } },
                    { binding: 1, resource: { buffer: outputBuffer } },
                    { binding: 2, resource: { buffer: uniformBuffer } }
                ]
            });

            // Execute
            const commandEncoder = this.gpuDevice.createCommandEncoder();
            const passEncoder = commandEncoder.beginComputePass();
            passEncoder.setPipeline(this.headlightPipeline);
            passEncoder.setBindGroup(0, bindGroup);
            passEncoder.dispatchWorkgroups(Math.ceil(lightCount / 64));
            passEncoder.end();

            commandEncoder.copyBufferToBuffer(outputBuffer, 0, readbackBuffer, 0, outputSize);
            this.gpuDevice.queue.submit([commandEncoder.finish()]);

            // Readback
            await readbackBuffer.mapAsync(GPU_MAP_MODE.READ);
            const resultData = new Float32Array(readbackBuffer.getMappedRange().slice(0));
            readbackBuffer.unmap();

            // Extract results
            const positions = new Float32Array(lightCount * 3);
            const targetPositions = new Float32Array(lightCount * 3);

            for (let i = 0; i < lightCount; i++) {
                const inOffset = i * 8;
                const outOffset = i * 3;
                positions[outOffset] = resultData[inOffset];
                positions[outOffset + 1] = resultData[inOffset + 1];
                positions[outOffset + 2] = resultData[inOffset + 2];
                targetPositions[outOffset] = resultData[inOffset + 4];
                targetPositions[outOffset + 1] = resultData[inOffset + 5];
                targetPositions[outOffset + 2] = resultData[inOffset + 6];
            }

            // Cleanup
            inputBuffer.destroy();
            outputBuffer.destroy();
            readbackBuffer.destroy();
            uniformBuffer.destroy();

            return {
                positions,
                targetPositions,
                gpuTime: performance.now() - startTime
            };

        } catch (e) {
            return null;
        }
    }

    private _computeHeadlightsCPU(
        lights: Array<{
            position: THREE.Vector3;
            offsetDistance: number;
            manualOffset: THREE.Vector3;
            isActive: boolean;
            targetPosition: THREE.Vector3;
        }>,
        cameraPosition: THREE.Vector3,
        cameraQuaternion: THREE.Quaternion,
        startTime: number
    ): HeadlightResult {
        const lightCount = lights.length;
        const positions = new Float32Array(lightCount * 3);
        const targetPositions = new Float32Array(lightCount * 3);

        for (let i = 0; i < lightCount; i++) {
            const light = lights[i];
            const outOffset = i * 3;

            if (!light.isActive) {
                positions[outOffset] = light.position.x;
                positions[outOffset + 1] = light.position.y;
                positions[outOffset + 2] = light.position.z;
                targetPositions[outOffset] = light.targetPosition.x;
                targetPositions[outOffset + 1] = light.targetPosition.y;
                targetPositions[outOffset + 2] = light.targetPosition.z;
            } else {
                // Calculate offset using pre-allocated vectors
                FikoWebGPUCameraOps._tempVec3.set(0, 0, light.offsetDistance);
                FikoWebGPUCameraOps._tempVec3.applyQuaternion(cameraQuaternion);

                positions[outOffset] = cameraPosition.x + FikoWebGPUCameraOps._tempVec3.x + light.manualOffset.x;
                positions[outOffset + 1] = cameraPosition.y + FikoWebGPUCameraOps._tempVec3.y + light.manualOffset.y;
                positions[outOffset + 2] = cameraPosition.z + FikoWebGPUCameraOps._tempVec3.z + light.manualOffset.z;
                targetPositions[outOffset] = cameraPosition.x;
                targetPositions[outOffset + 1] = cameraPosition.y;
                targetPositions[outOffset + 2] = cameraPosition.z;
            }
        }

        return {
            positions,
            targetPositions,
            gpuTime: performance.now() - startTime
        };
    }

    // ========================================================================
    // UTILITY
    // ========================================================================

    get available(): boolean {
        return this.isAvailable;
    }

    getStats() {
        return {
            ...this.stats,
            avgGPUTime: this.stats.cameraOpsDispatches > 0
                ? this.stats.totalGPUTime / this.stats.cameraOpsDispatches
                : 0
        };
    }

    dispose(): void {
        // Clear buffer pool
        this.bufferPool.forEach(pool => {
            pool.forEach(buffer => buffer.destroy());
        });
        this.bufferPool.clear();

        // Clear cached buffers
        this.cachedUniformBuffer?.destroy();
        this.cachedResultBuffer?.destroy();

        // Clear device reference
        this.gpuDevice = null;
        this.gpuAdapter = null;
        this.isAvailable = false;

        FikoWebGPUCameraOps.instance = null;
    }
}

// Singleton export
export const fikoWebGPUCameraOps = FikoWebGPUCameraOps.getInstance();
